import { useState, useRef } from "react"
import { Box, Button, CircularProgress, Popover, TextField, Typography } from "@mui/material"
import { Sparkles } from "lucide-react"
import { useToast } from "@crm-src/hooks/use-toast"
import { useTypewriter } from "@crm-src/hooks/useTypewriter"
import { toSubjectLine } from "@crm-src/lib/ai-text"
import api from "@crm-src/lib/api"

interface InlineAiGeneratorProps {
  onGenerate: (content: string) => void
  onGeneratePackage?: (data: { title: string; content: string }) => void // For generating both title and content
  fieldType: 'short' | 'long' | 'package' // package generates both title and content
  context?: string // What this field is for
  placeholder?: string
  currentValue?: string
}

export function InlineAiGenerator({
  onGenerate,
  onGeneratePackage,
  fieldType,
  context = "",
  placeholder = "Describe what you want...",
  currentValue = ""
}: InlineAiGeneratorProps) {
  const [open, setOpen] = useState(false)
  const [prompt, setPrompt] = useState("")
  const [isGenerating, setIsGenerating] = useState(false)
  const { toast } = useToast()
  const anchorRef = useRef<HTMLButtonElement>(null)
  const animateText = useTypewriter()

  // OpenAI requests go through the authenticated server-side proxy.
  // Helper that mirrors openai.chat.completions.create() but without exposing
  // the API key to the browser.
  const callAi = async (params: {
    systemMessage: string
    userPrompt: string
    model?: string
    max_tokens?: number
    temperature?: number
    // top_k:0 = NO knowledge-base grounding (copywriting, not RAG). Pass 0 ONLY
    // for SHORT subject/title generation — the content persona was dumping internal
    // KB style/config docs into the field. LONG body + package content OMIT it so
    // the gateway's default KB grounding stands (cite real product facts).
    top_k?: number
  }): Promise<string> => {
    // Route through the shared CRM axios client (NOT a raw fetch): it adds the
    // /api/v1 prefix + the crm token AND — crucially — RE-MINTS the short-lived
    // crm token and retries on a 401. A raw fetch just failed on an expired token
    // (the "AI generator not working" 401). Response shape is the gateway's
    // { text, sources, usage }; read .text first (OpenAI shapes kept as fallbacks).
    const resp = await api.post("/ai/generate", {
      prompt: params.userPrompt,
      system_prompt: params.systemMessage,
      model: params.model || "gpt-3.5-turbo",
      max_tokens: params.max_tokens,
      temperature: params.temperature,
      ...(params.top_k !== undefined ? { top_k: params.top_k } : {}),
    })
    const json = resp.data
    return json?.text || json?.choices?.[0]?.message?.content || json?.content || ""
  }

  const generateContent = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter a prompt",
        variant: "destructive",
      })
      return
    }

    setIsGenerating(true)

    try {
      if (fieldType === 'package' && onGeneratePackage) {
        // Generate both title and content together
        const systemMessage = `You are an expert copywriter for XOP Networks, a leading conferencing technology company.

Company Context:
XOP Networks designs and delivers conferencing solutions for mission-critical industries including aviation, oil & gas, power generation, and public safety.

Task: Generate BOTH a title and content for ${context || 'a newsletter section'}.

Requirements:
- Title: Keep it under 60 characters, catchy and clear, NO quotation marks
- Content: Write 2-3 paragraphs, professional yet engaging
- Make it relevant to B2B technology/conferencing industry
- Focus on value and benefits
- Use active voice

Return your response in this EXACT format:
TITLE: [your title here]
CONTENT: [your content here]`

        const generated = (await callAi({
          systemMessage,
          userPrompt: prompt,
          model: "gpt-3.5-turbo",
          temperature: 0.8,
          max_tokens: 600
        })).trim()

        // Check if AI is asking for more information or is refusing to generate
        const needsMoreInfo = generated.toLowerCase().includes("need more") ||
                               generated.toLowerCase().includes("more specific") ||
                               generated.toLowerCase().includes("more details") ||
                               generated.toLowerCase().includes("please provide") ||
                               generated.toLowerCase().includes("can you clarify") ||
                               generated.toLowerCase().includes("i'm sorry") ||
                               generated.toLowerCase().includes("i cannot") ||
                               generated.toLowerCase().includes("i can only")

        if (needsMoreInfo) {
          toast({
            title: "💡 Need More Details",
            description: "Try a more specific prompt! Example: 'Write about how video conferencing improves remote team collaboration' or 'Describe the benefits of our secure communication platform'",
            variant: "default",
          })
          setIsGenerating(false)
          return // Don't close the popover, let user try again
        }

        // Parse the response - split by newlines and find TITLE and CONTENT sections
        const lines = generated.split('\n')
        let title = ''
        let content = ''
        let inContent = false

        for (const line of lines) {
          if (line.startsWith('TITLE:')) {
            title = line.replace('TITLE:', '').trim().replace(/^["']|["']$/g, '')
          } else if (line.startsWith('CONTENT:')) {
            content = line.replace('CONTENT:', '').trim()
            inContent = true
          } else if (inContent) {
            content += '\n' + line
          }
        }

        content = content.trim()

        if (title && content) {

          onGeneratePackage({ title, content })
          setOpen(false)
          setPrompt("")
          toast({
            title: "✨ Content Generated!",
            description: "Your content is ready to use",
          })
        } else {
          // Log for debugging but show friendly message to user
          toast({
            title: "🤔 Let's Try Again",
            description: "Please describe what you'd like to write about more specifically. Example: 'Success story about implementing conference systems in aviation'",
            variant: "default",
          })
          setIsGenerating(false)
          return // Don't close the popover
        }
      } else {
        // Generate single field
        const systemMessage = `You are an expert copywriter for XOP Networks, a leading conferencing technology company.

Company Context:
XOP Networks designs and delivers conferencing solutions for mission-critical industries including aviation, oil & gas, power generation, and public safety.

Task: Generate ${fieldType === 'short' ? 'a concise, engaging title/headline' : 'compelling, professional content'} for ${context || 'a newsletter'}.

${currentValue ? `Current content: "${currentValue}"\nImprove or replace this based on the prompt.` : ''}

Requirements:
- ${fieldType === 'short' ? 'Output a SINGLE line under 60 characters, catchy and clear — NEVER a greeting, email body, paragraph, or multiple sentences' : 'Write 2-3 paragraphs, professional yet engaging'}
- Make it relevant to B2B technology/conferencing industry
- Focus on value and benefits
- Use active voice
- ${fieldType === 'short' ? 'NO quotation marks or extra punctuation' : 'Include specific details when relevant'}

Return ONLY the generated ${fieldType === 'short' ? 'one-line title' : 'content'}, no explanations or markdown formatting.`

        const generatedRaw = (await callAi({
          systemMessage,
          userPrompt: prompt,
          model: "gpt-3.5-turbo",
          temperature: 0.8,
          // >= 64: the AI gateway rejects max_tokens < 64 (422). A tight cap for a
          // subject; toSubjectLine() below is the hard guard against a body answer.
          max_tokens: fieldType === 'short' ? 64 : 500,
          // short subject only: no KB grounding (it dumped internal KB docs). Long
          // body content keeps the gateway's default grounding (real product facts).
          ...(fieldType === 'short' ? { top_k: 0 } : {}),
        })).trim().replace(/^["']|["']$/g, '') // Remove quotes
        // A SHORT field is an email subject — clamp to one subject line.
        const generated = fieldType === 'short' ? toSubjectLine(generatedRaw) : generatedRaw

        // Check if AI is asking for more information
        const needsMoreInfo = generated.toLowerCase().includes("need more") ||
                               generated.toLowerCase().includes("more specific") ||
                               generated.toLowerCase().includes("more details") ||
                               generated.toLowerCase().includes("please provide") ||
                               generated.toLowerCase().includes("can you clarify") ||
                               generated.toLowerCase().includes("i'm sorry") ||
                               generated.toLowerCase().includes("i cannot") ||
                               generated.toLowerCase().includes("i can only")

        if (needsMoreInfo || !generated) {
          toast({
            title: "💡 Need More Details",
            description: fieldType === 'short'
              ? "Try something like: 'quarterly business review meeting' or 'new product launch announcement'"
              : "Try something like: 'benefits of cloud-based conferencing' or 'how we improved emergency communications'",
            variant: "default",
          })
          setIsGenerating(false)
          return // Don't close the popover, let user try again
        }

        if (generated) {
          setOpen(false)
          setPrompt("")
          // Typewriter: let the text "write itself" into the field, like an AI
          // agent composing it (the backend returns it all at once).
          animateText(generated, onGenerate, {
            onDone: () => toast({
              title: "✨ Content Generated!",
              description: "Your content is ready to use",
            }),
          })
        }
      }
    } catch (error: any) {
      console.error("AI Generation Error:", error)
      toast({
        title: "⚠️ Something Went Wrong",
        description: "Please try again or rephrase your request",
        variant: "destructive",
      })
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <>
      <Button
        ref={anchorRef}
        type="button"
        size="small"
        variant="outlined"
        color="primary"
        onClick={() => setOpen(true)}
        startIcon={<Sparkles size={14} />}
        sx={{ height: 32 }}
      >
        Generate
      </Button>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 320, p: 2 } } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box>
            <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Generate with AI</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
              {context || `Generate ${fieldType === 'short' ? 'a title' : 'content'}`}
            </Typography>
          </Box>

          {fieldType === 'short' ? (
            <TextField
              fullWidth
              size="small"
              placeholder={placeholder}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  generateContent()
                }
              }}
            />
          ) : (
            <TextField
              fullWidth
              multiline
              size="small"
              placeholder={placeholder}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
            />
          )}

          <Button
            onClick={generateContent}
            disabled={isGenerating || !prompt.trim()}
            variant="contained"
            color="primary"
            fullWidth
            size="small"
            startIcon={
              isGenerating
                ? <CircularProgress size={16} color="inherit" />
                : <Sparkles size={16} />
            }
          >
            {isGenerating ? "Generating..." : "Generate"}
          </Button>
        </Box>
      </Popover>
    </>
  )
}
